import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import useUserFromToken from "../../hooks/useUserFromToken"
import { handleToast } from "../../utils/messages"
import {
  getRvnnrCountryAggregations,
  getRvnnrCountryCompanies,
  RvnnrCountryAggregation,
  RvnnrCompanySummary,
} from "../submissions/service/api"
import { getDashboardsUrl } from "./service/api"

const formatVolume = (value: number | null) => {
  if (value === null || value === undefined) return "-"
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value)
}

const formatDate = (value: string | null) => {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return d.toISOString().slice(0, 10)
}

const Dashboards = () => {
  const { t } = useTranslation()
  const { isRrr } = useUserFromToken()

  const [dashboardsUrl, setDashboardsUrl] = useState("")
  const [aggregations, setAggregations] = useState<RvnnrCountryAggregation[]>(
    [],
  )
  const [plants, setPlants] = useState<RvnnrCompanySummary[]>([])
  const [loadingRvnnr, setLoadingRvnnr] = useState(false)

  useEffect(() => {
    getDashboardsUrl().then(({ data, errors }) => {
      if (errors) {
        handleToast(errors, "error")
      }
      if (data) {
        setDashboardsUrl(data)
      }
    })
  }, [])

  useEffect(() => {
    if (!isRrr) return
    setLoadingRvnnr(true)
    Promise.all([
      getRvnnrCountryAggregations({}),
      getRvnnrCountryCompanies({}),
    ])
      .then(([aggRes, compRes]) => {
        if (aggRes.errors) handleToast(aggRes.errors, "error")
        if (compRes.errors) handleToast(compRes.errors, "error")
        if (aggRes.data) setAggregations(aggRes.data)
        if (compRes.data) setPlants(compRes.data)
      })
      .finally(() => setLoadingRvnnr(false))
  }, [isRrr])

  const plantsByCountry = useMemo(() => {
    const grouped: Record<string, RvnnrCompanySummary[]> = {}
    plants.forEach((p) => {
      const key = p.countryName || "-"
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(p)
    })
    return grouped
  }, [plants])

  return (
    <>
      <h1 className="mb-5">{t("Dashboards")}</h1>

      {isRrr && (
        <div className="mb-5">
          <h3 className="mb-3">{t("Country overview")}</h3>
          {loadingRvnnr ? (
            <div>{t("Loading...")}</div>
          ) : aggregations.length === 0 ? (
            <div className="mb-3">{t("There is no data to display")}</div>
          ) : (
            <table className="table table-light table-bordered mb-4">
              <thead>
                <tr>
                  <th>{t("Country")}</th>
                  <th>{t("Companies with submissions")}</th>
                  <th>{t("Submissions")}</th>
                  <th>{t("Total gross volume (kg)")}</th>
                  <th>{t("Total net volume (kg)")}</th>
                </tr>
              </thead>
              <tbody>
                {aggregations.map((row) => (
                  <tr key={row.countryId}>
                    <td>{row.countryName}</td>
                    <td>{row.companyCount}</td>
                    <td>{row.submissionCount}</td>
                    <td>
                      {row.canShowDetails
                        ? formatVolume(row.totalVolumeGross)
                        : t("Hidden (75% rule)")}
                    </td>
                    <td>
                      {row.canShowDetails
                        ? formatVolume(row.totalVolumeNet)
                        : t("Hidden (75% rule)")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 className="mt-5 mb-3">{t("Plants in your countries")}</h3>
          {Object.keys(plantsByCountry).length === 0 ? (
            <div className="mb-3">{t("There is no data to display")}</div>
          ) : (
            Object.entries(plantsByCountry).map(([country, rows]) => (
              <div key={country} className="mb-4">
                <h6 className="text-muted mb-2">{country}</h6>
                <table className="table table-light table-bordered">
                  <thead>
                    <tr>
                      <th>{t("Plant")}</th>
                      <th>{t("Company")}</th>
                      <th>{t("Registration date")}</th>
                      <th>{t("VinylPlus agreement")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.plantId}>
                        <td>{p.plantName}</td>
                        <td>{p.parentCompanyName || "-"}</td>
                        <td>{formatDate(p.registrationDate)}</td>
                        <td>{p.hasVinylPlusAgree ? t("Yes") : t("No")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}

      {dashboardsUrl ? (
        <iframe
          title={t("Dashboards")}
          src={dashboardsUrl}
          className="srs-iframe"
          style={isRrr ? { height: "70vh" } : undefined}
        />
      ) : (
        !isRrr && <div>{t("There is no data to display")}</div>
      )}
    </>
  )
}

export default Dashboards
